import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { loadingTrue, loadingFalse } from "../../Reducer/loaderSlice";
import Handler from "../../Helpers/Handler";
import moment from "moment";
import MainLayout from "../../Partials/MainLayout";
import FeedLayout from "../../Partials/FeedLayout";

const ADMIN_ID = 1;
const POLL_INTERVAL = 5000;
const FALLBACK_AVATAR = "https://ui-avatars.com/api/?background=1e3a8a&color=fff&name=Admin";

const UserAdminChat = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const [message, setMessage]       = useState("");
    const [messages, setMessages]     = useState([]);
    const [chatId, setChatId]         = useState(null);
    const [activeMenu, setActiveMenu] = useState(null);
    const [showClearConfirm, setShowClearConfirm] = useState(false);
    const [sending, setSending]       = useState(false);

    const bottomRef   = useRef(null);
    const pollRef     = useRef(null);
    const msgBoxRef   = useRef(null);

    /* ── Close menu on outside click ── */
    useEffect(() => {
        const close = () => setActiveMenu(null);
        document.addEventListener("click", close);
        return () => document.removeEventListener("click", close);
    }, []);

    /* ── Step 1: Start or get existing chat with admin ── */
    useEffect(() => {
        const init = async () => {
            dispatch(loadingTrue());
            try {
                const r = await Handler({
                    method: "post",
                    url: "/admin_chat/start.php",
                    data: { user_id: ADMIN_ID }
                });
                if (r?.success) {
                    setChatId(r.data.chat_id);
                }
            } catch (e) {
                Handler(e);
            }
            dispatch(loadingFalse());
        };
        init();
    }, [dispatch]);

    /* ── Step 2: Load messages once chatId is ready ── */
    const loadMessages = useCallback(async (showLoader = false) => {
        if (showLoader) dispatch(loadingTrue());
        try {
            const r = await Handler({
                method: "get",
                url: `/admin_chat/message.php`
            });
            if (r?.data?.list) setMessages(r.data.list);
        } catch (e) { Handler(e); }
        if (showLoader) dispatch(loadingFalse());
    }, [dispatch]);

    useEffect(() => {
        if (!chatId) return;
        loadMessages(true);
        Handler({ method: "post", url: "/admin_chat/mark_read.php", data: {} }).catch(() => {});
        pollRef.current = setInterval(() => loadMessages(false), POLL_INTERVAL);
        return () => clearInterval(pollRef.current);
    }, [chatId, loadMessages]);

    /* ── Scroll to bottom — direct container only ── */
    useEffect(() => {
        if (msgBoxRef.current) {
            msgBoxRef.current.scrollTop = msgBoxRef.current.scrollHeight;
        }
    }, [messages]);

    /* ── Send message ── */
    const sendMessage = async () => {
        if (!message.trim() || !chatId || sending) return;
        const text = message.trim();
        setMessage("");
        setSending(true);

        const temp = {
            id: `temp-${Date.now()}`,
            text,
            is_me: true,
            created_at: new Date().toISOString(),
            temp: true
        };
        setMessages(prev => [...prev, temp]);

        try {
            await Handler({
                method: "post",
                url: "/admin_chat/send.php",
                data: { chat_id: chatId, message: text, message_type: "TEXT" }
            });
            await loadMessages(false);
        } catch (e) {
            Handler(e);
            setMessages(prev => prev.filter(m => m.id !== temp.id));
            setMessage(text);
        }
        setSending(false);
    };

    /* ── Delete for me ── */
    const deleteMessage = async (messageId) => {
        setActiveMenu(null);
        try {
            await Handler({
                method: "post",
                url: "/admin_chat/delete.php",
                data: { action: "delete_message", message_id: messageId }
            });
            setMessages(prev => prev.filter(m => m.id !== messageId));
        } catch (e) { Handler(e); }
    };

    /* ── Delete for everyone ── */
    const deleteForEveryone = async (messageId) => {
        setActiveMenu(null);
        try {
            await Handler({
                method: "post",
                url: "/admin_chat/delete.php",
                data: { action: "delete_for_everyone", message_id: messageId }
            });
            setMessages(prev => prev.filter(m => m.id !== messageId));
        } catch (e) { Handler(e); }
    };

    /* ── Clear entire chat ── */
    const deleteChat = async () => {
        setShowClearConfirm(false);
        dispatch(loadingTrue());
        try {
            await Handler({
                method: "post",
                url: "/admin_chat/delete.php",
                data: { action: "delete_chat", business_user_id: ADMIN_ID }
            });
            navigate(-1);
        } catch (e) { Handler(e); }
        dispatch(loadingFalse());
    };

    /* ── Keyboard send ── */
    const handleKeyDown = (e) => {
        if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendMessage(); }
    };

    return (
        <MainLayout>
            <FeedLayout>
                <div className="comm-page" style={{ paddingBottom: 0 }}>

                    {/* ── Page Header ── */}
                    <div className="comm-header">
                        <h2>
                            Admin Support
                        </h2>
                        <button className="back-btn" onClick={() => navigate(-1)}>
                            <i className="bx bx-arrow-back" /> Back
                        </button>
                    </div>

                    {/* ── Chat Box ── */}
                    <div className="comm-chat-wrap">

                        {/* ── Messages Area ── */}
                        <div
                            ref={msgBoxRef}
                            className="chat-messages-area"
                        >
                            {messages.length === 0 ? (
                                <div className="comm-empty">
                                    <i className="bx bx-chat" />
                                    <p>No messages yet. Send a message to get support from Admin.</p>
                                </div>
                            ) : (
                                messages.map((m, i) => {
                                    const isMe = m.is_me;
                                    return (
                                        <div
                                            key={m.id ?? i}
                                            className={`chat-bubble-wrap${isMe ? " own" : ""}`}
                                        >
                                            {/* Admin avatar */}
                                            <div className="chat-bubble-avatar">
                                                <img
                                                    src={FALLBACK_AVATAR}
                                                    alt="Admin"
                                                    style={{
                                                        width: "100%",
                                                        height: "100%",
                                                        borderRadius: "50%",
                                                        objectFit: "cover"
                                                    }}
                                                    onError={(e) => {
                                                        e.target.style.display = "none";
                                                    }}
                                                />
                                            </div>

                                            <div className="chat-bubble" style={{ opacity: m.temp ? 0.6 : 1 }}>
                                                {!isMe && (
                                                    <div className="bubble-sender">Support Admin</div>
                                                )}
                                                <span>{m.text || m.message}</span>
                                                <div className="bubble-time">
                                                    {moment(m.created_at).format("hh:mm A")}

                                                    {/* Delete dropdown — own messages only, not temp */}
                                                    {isMe && !m.temp && (
                                                        <span
                                                            style={{
                                                                position: "relative",
                                                                display: "inline-block",
                                                                marginLeft: 6,
                                                                cursor: "pointer",
                                                                verticalAlign: "middle"
                                                            }}
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                setActiveMenu(activeMenu === m.id ? null : m.id);
                                                            }}
                                                        >
                                                            <i className="bx bx-dots-vertical-rounded" style={{ fontSize: 14, color: "#94a3b8" }} />

                                                            {activeMenu === m.id && (
                                                                <div
                                                                    onClick={(e) => e.stopPropagation()}
                                                                    style={{
                                                                        position: "absolute",
                                                                        bottom: 22,
                                                                        right: 0,
                                                                        background: "#fff",
                                                                        borderRadius: 10,
                                                                        boxShadow: "0 4px 20px rgba(0,0,0,0.15)",
                                                                        zIndex: 100,
                                                                        minWidth: 175,
                                                                        overflow: "hidden"
                                                                    }}
                                                                >
                                                                    <button
                                                                        onClick={() => deleteMessage(m.id)}
                                                                        style={dropItemStyle}
                                                                    >
                                                                        <i className="bx bx-hide" style={{ marginRight: 8 }} />
                                                                        Delete for me
                                                                    </button>
                                                                    <button
                                                                        onClick={() => deleteForEveryone(m.id)}
                                                                        style={{ ...dropItemStyle, color: "#ef4444" }}
                                                                    >
                                                                        <i className="bx bx-trash" style={{ marginRight: 8 }} />
                                                                        Delete for everyone
                                                                    </button>
                                                                </div>
                                                            )}
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })
                            )}
                            <div ref={bottomRef} />
                        </div>

                        {/* ── Input Bar ── */}
                        <div className="chat-input-bar">
                            <input
                                type="text"
                                placeholder="Type a message to Admin… (Enter to send)"
                                value={message}
                                onChange={(e) => setMessage(e.target.value)}
                                onKeyDown={handleKeyDown}
                                disabled={sending}
                                maxLength={1000}
                            />

                            <button
                                className="chat-icon-btn send"
                                onClick={sendMessage}
                                disabled={sending || !message.trim()}
                                title="Send"
                            >
                                {sending ? (
                                    <i className="bx bx-loader-alt bx-spin" />
                                ) : (
                                    <i className="bx bx-send" />
                                )}
                            </button>
                        </div>
                    </div>
                </div>

                {/* ── Confirm Clear Dialog ── */}
                {showClearConfirm && (
                    <div
                        onClick={() => setShowClearConfirm(false)}
                        style={{
                            position: "fixed", inset: 0,
                            background: "rgba(0,0,0,0.4)",
                            zIndex: 999,
                            display: "flex", alignItems: "center", justifyContent: "center"
                        }}
                    >
                        <div
                            onClick={(e) => e.stopPropagation()}
                            style={{
                                background: "#fff", borderRadius: 14,
                                padding: "24px", maxWidth: 340, width: "90%",
                                boxShadow: "0 8px 32px rgba(0,0,0,0.18)"
                            }}
                        >
                            <h6 style={{ fontWeight: 700, marginBottom: 6, color: "#1a365d" }}>Clear Conversation?</h6>
                            <p style={{ color: "#64748b", fontSize: "0.85rem", marginBottom: 18 }}>
                                This removes the chat from <strong>your view only</strong>. Admin will still see all messages.
                            </p>
                            <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
                                <button
                                    onClick={() => setShowClearConfirm(false)}
                                    style={{
                                        padding: "7px 16px", borderRadius: 8,
                                        border: "1px solid #e2e8f0", background: "#f8fafc",
                                        cursor: "pointer", fontSize: "0.85rem"
                                    }}
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={deleteChat}
                                    style={{
                                        padding: "7px 16px", borderRadius: 8,
                                        border: "none", background: "#ef4444",
                                        color: "#fff", cursor: "pointer",
                                        fontWeight: 600, fontSize: "0.85rem"
                                    }}
                                >
                                    Yes, Clear
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </FeedLayout>
        </MainLayout>
    );
};

const dropItemStyle = {
    display: "flex",
    alignItems: "center",
    width: "100%",
    padding: "10px 14px",
    fontSize: "0.83rem",
    background: "none",
    border: "none",
    borderBottom: "1px solid #f1f5f9",
    cursor: "pointer",
    color: "#334155",
    whiteSpace: "nowrap",
    transition: "background 0.15s"
};

export default UserAdminChat;